/**
 * OPLYX — Iter 117 — Offline-first POS — IndexedDB layer
 *
 * Single source of truth for the cached catalog, customers and the pending sales
 * queue. Wraps `idb` to give the rest of the app a tiny ergonomic surface.
 *
 * Stores:
 *   `bootstrap` (key/value) — last successful /api/pos/bootstrap snapshot meta
 *   `products`              — keyed by `id`
 *   `categories`            — keyed by `id`
 *   `customers`             — keyed by `id`
 *   `pending_sales`         — keyed by `idempotency_key`
 *   `pending_nfces`         — keyed by `idempotency_key` (NFC-e offline queue)
 *
 * NOTE: data is per-browser. We DO NOT scope by user/business inside the DB name
 * because a single till is operated by one business at a time. If a user logs out
 * and another business logs in on the same machine, call `wipeOfflineDB()`.
 */
import { openDB } from "idb";

const DB_NAME = "oplyx-pos-v1";
const DB_VERSION = 4;

let _dbPromise = null;
function db() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(d) {
        if (!d.objectStoreNames.contains("bootstrap")) d.createObjectStore("bootstrap");
        if (!d.objectStoreNames.contains("products")) d.createObjectStore("products", { keyPath: "id" });
        if (!d.objectStoreNames.contains("categories")) d.createObjectStore("categories", { keyPath: "id" });
        if (!d.objectStoreNames.contains("customers")) d.createObjectStore("customers", { keyPath: "id" });
        if (!d.objectStoreNames.contains("pending_sales"))
          d.createObjectStore("pending_sales", { keyPath: "idempotency_key" });
        // v2: NFC-e offline queue (PDV sem conexão com o backend)
        if (!d.objectStoreNames.contains("pending_nfces"))
          d.createObjectStore("pending_nfces", { keyPath: "idempotency_key" });
        // v3: offline-first comandas
        if (!d.objectStoreNames.contains("open_tabs"))
          d.createObjectStore("open_tabs", { keyPath: "id" });
        if (!d.objectStoreNames.contains("pending_tab_mutations"))
          d.createObjectStore("pending_tab_mutations", { keyPath: "_mutation_id" });
        // v4: cash session (evita bloqueio de vendas numa recarga offline)
        if (!d.objectStoreNames.contains("cash_session"))
          d.createObjectStore("cash_session", { keyPath: "branch_id" });
      },
    });
  }
  return _dbPromise;
}

// ─── Bootstrap snapshot (catalog) ───────────────────────────────────
export async function saveBootstrap(snap) {
  const d = await db();
  const tx = d.transaction(
    ["bootstrap", "products", "categories", "customers", "open_tabs"],
    "readwrite"
  );
  await tx.objectStore("bootstrap").put({ ...snap, _saved_at: new Date().toISOString() }, "last");
  // Cache da prontidão fiscal (PDV offline)
  if (snap.fiscal_config) {
    await tx.objectStore("bootstrap").put(
      { ...snap.fiscal_config, _saved_at: new Date().toISOString() },
      "fiscal_config"
    );
  }
  // Replace catalog atomically (clear+put). Small datasets — fine.
  const ps = tx.objectStore("products");
  await ps.clear();
  for (const p of snap.products || []) await ps.put(p);
  const cs = tx.objectStore("categories");
  await cs.clear();
  for (const c of snap.categories || []) await cs.put(c);
  const cu = tx.objectStore("customers");
  await cu.clear();
  for (const c of snap.customers || []) await cu.put(c);
  // Merge server-side open tabs while preserving locally-created offline ones
  if ("open_tabs" in snap) {
    const tabStore = tx.objectStore("open_tabs");
    const existing = await tabStore.getAll();
    const offlineCreated = existing.filter((t) => t._offline_created);
    await tabStore.clear();
    for (const t of snap.open_tabs || []) await tabStore.put(t);
    for (const t of offlineCreated) await tabStore.put(t);
  }
  await tx.done;
}

export async function getBootstrap() {
  const d = await db();
  return d.get("bootstrap", "last");
}

export async function getCachedProducts() {
  const d = await db();
  return d.getAll("products");
}

export async function getCachedCategories() {
  const d = await db();
  return d.getAll("categories");
}

export async function getCachedCustomers() {
  const d = await db();
  return d.getAll("customers");
}

// ─── Pending sales queue ────────────────────────────────────────────
export async function queueSale(salePayload) {
  // Must include idempotency_key, client_created_at, offline_origin=true
  const d = await db();
  await d.put("pending_sales", { ...salePayload, _queued_at: new Date().toISOString() });
}

export async function listPendingSales() {
  const d = await db();
  return d.getAll("pending_sales");
}

export async function countPendingSales() {
  const d = await db();
  return d.count("pending_sales");
}

export async function removePendingSale(idempotency_key) {
  const d = await db();
  await d.delete("pending_sales", idempotency_key);
}

export async function wipeOfflineDB() {
  const d = await db();
  const stores = [
    "bootstrap", "products", "categories", "customers",
    "pending_sales", "pending_nfces", "open_tabs", "pending_tab_mutations",
    "cash_session",
  ];
  const tx = d.transaction(stores, "readwrite");
  await Promise.all(stores.map((s) => tx.objectStore(s).clear()));
  await tx.done;
}

// ─── Open tabs cache ────────────────────────────────────────────────
export async function saveTab(tab) {
  const d = await db();
  await d.put("open_tabs", tab);
}

export async function getTab(id) {
  const d = await db();
  return d.get("open_tabs", id);
}

export async function getAllTabs() {
  const d = await db();
  return d.getAll("open_tabs");
}

export async function deleteTab(id) {
  const d = await db();
  await d.delete("open_tabs", id);
}

export async function clearTabs() {
  const d = await db();
  await d.clear("open_tabs");
}

// ─── Cash session cache (evita bloqueio offline numa recarga) ────────
export async function saveCashSession(session) {
  const d = await db();
  await d.put("cash_session", { ...session, _saved_at: new Date().toISOString() });
}

export async function getCashSession(branchId) {
  const d = await db();
  return d.get("cash_session", branchId);
}

export async function clearCashSession(branchId) {
  const d = await db();
  await d.delete("cash_session", branchId);
}

// ─── Pending tab mutations queue ────────────────────────────────────
export async function queueTabMutation(mut) {
  const d = await db();
  await d.put("pending_tab_mutations", {
    ...mut,
    _queued_at: mut._queued_at || new Date().toISOString(),
  });
}

export async function getTabMutations(tabId) {
  const d = await db();
  const all = await d.getAll("pending_tab_mutations");
  return all.filter((m) => m.tab_id === tabId);
}

export async function getAllPendingMutations() {
  const d = await db();
  return d.getAll("pending_tab_mutations");
}

export async function ackTabMutation(mutationId) {
  const d = await db();
  await d.delete("pending_tab_mutations", mutationId);
}

export async function countPendingTabMutations() {
  const d = await db();
  return d.count("pending_tab_mutations");
}

// ─── Fiscal config cache (PDV offline) ────────────────────────────────
export async function saveFiscalConfigCache(config) {
  const d = await db();
  await d.put("bootstrap", { ...config, _saved_at: new Date().toISOString() }, "fiscal_config");
}

export async function getFiscalConfigCache() {
  const d = await db();
  return d.get("bootstrap", "fiscal_config");
}

// ─── Pending NFC-e queue (PDV sem conexão com o backend) ────────────
export async function queueNfce(entry) {
  const d = await db();
  await d.put("pending_nfces", { ...entry, _queued_at: entry._queued_at || new Date().toISOString() });
}

export async function listPendingNfces() {
  const d = await db();
  return d.getAll("pending_nfces");
}

export async function countPendingNfces() {
  const d = await db();
  return d.count("pending_nfces");
}

export async function removePendingNfce(idempotency_key) {
  const d = await db();
  await d.delete("pending_nfces", idempotency_key);
}
